import BookingModule from "@/store/modules/booking";
import ComplianceModule from "@/store/modules/compliance";
import DeviceModule from "@/store/modules/device";
import UserModule from "@/store/modules/user";
import CustomersModule from "@/store/modules/customers";
import QRCode from "qrcode";
import dayjs from "dayjs";
import {
  IAustriaAdditionalInfo,
  IBelgiumAdditionalInfo,
  IBookingReceipt,
  ICroatiaAdditionalInfo,
  IFranceAdditionalInfo,
  IGermanyAdditionalInfo,
  IItalyAdditionalInfo,
  ISpainAdditionalInfo,
  IVatToPrint,
  VatLineType,
} from "@/interfaces/receipts";
import { OrderJson } from "@smeetz/orders";
import { VatCalculation } from "@smeetz/compliance-common";
import { sumArray } from "./common";
import { FdmRef, IProductSlotBooking } from "@/types/db/product-slot-booking";
import { OrderType } from "@smeetz/order-common";
import { classifyOrderSession } from "@smeetz/compliance-common-v2";
import { LogLevel, LogService } from "@/services/logging";
import NotificationService from "@/services/notification.service";
import { checkAndProcessReprint, efrDict } from "./compliance";
import { processPrintEventV2 } from "./compliance-v2";
import { orderTypeToReceiptType } from "./order-session";
import { formatDateTime } from "./dates";
import {
  isAustrianCompliant,
  isBelgianCompliant,
  isCroatianCompliant,
  isFrenchCompliant,
  isGermanCompliant,
  isItalianCompliant,
  isSpanishCompliant,
} from "@/utils/vue-helpers/compliance";
import i18n from "@/lang";
const t: (key: string, ...args: unknown[]) => string = (i18n.global as any).t;

export interface ComplianceResult {
  shouldAbort: boolean;
}

/**
 * The FDM's VAT breakdown is what gets printed, which would silently mask a
 * drift in the POS's own VAT maths — so log any disagreement.
 *
 * Lines are matched by rate rather than by position: the FDM is under no
 * obligation to return them in our order, and a reordering is not a difference.
 */
function compareAndLogVatDifferences(
  vatsFromFdm: VatCalculation[],
  vatsCalculated: (IVatToPrint | null)[]
) {
  const posVats = vatsCalculated.filter((vat): vat is IVatToPrint => !!vat);
  if (vatsFromFdm.length !== posVats.length) {
    LogService.info(
      "VAT difference: the FDM and the POS produced a different number of VAT lines",
      { vatsFromFdm, vatsCalculated: posVats }
    );
    return;
  }

  // the POS writes the rate with a trailing "%", the FDM as a number
  const rateOf = (vat: IVatToPrint) => vat.rate.replace("%", "");

  for (const fdmVat of vatsFromFdm) {
    const rate = Number(fdmVat.rate || 0).toFixed(2);
    const posVat = posVats.find((vat) => rateOf(vat) === rate);
    if (
      !posVat ||
      Number(fdmVat.vatAmount || 0).toFixed(2) !== posVat.vatAmount ||
      Number(fdmVat.taxableAmount || 0).toFixed(2) !== posVat.amountExclVat
    ) {
      LogService.info("VAT difference between the FDM and the POS", {
        vatsFromFdm,
        vatsCalculated: posVats,
      });
      return;
    }
  }
}

async function applyAustriaSignature(
  receiptInfo: IBookingReceipt,
  booking: IProductSlotBooking
): Promise<void> {
  receiptInfo.isSignatureRequired = true;
  const baseOrderSession = booking.orderSessions.find(
    (os) => os.id === BookingModule.currentOrderSessionId
  );
  const austriaAdditionalInfo: IAustriaAdditionalInfo = {
    qrCode: "",
    receiptNumber: "",
    posId: DeviceModule.hardwareLocation?.deviceUid || "",
    fiskalyCashRegisterId:
      DeviceModule.hardwareLocation?.FiskalyCashRegisterId || "",
  };
  if (baseOrderSession?.signatureInfoAT) {
    receiptInfo.isSigned = baseOrderSession.signatureInfoAT.signed;
    austriaAdditionalInfo.qrCode = await QRCode.toDataURL(
      baseOrderSession.signatureInfoAT.qrCodeData
    );
    austriaAdditionalInfo.receiptNumber =
      baseOrderSession.signatureInfoAT.receiptNumber;
    austriaAdditionalInfo.receiptType = orderTypeToReceiptType(
      baseOrderSession.orderType
    );
  } else {
    receiptInfo.isSigned = false;
  }
  receiptInfo.austriaAdditionalInfo = austriaAdditionalInfo;
}

async function applyGermanySignature(
  receiptInfo: IBookingReceipt,
  booking: IProductSlotBooking
): Promise<ComplianceResult> {
  let reprintResponse;
  try {
    const { requiresReprint, result, error, undeterminedFiscalState } =
      await checkAndProcessReprint(booking);
    reprintResponse = result;
    if (undeterminedFiscalState) {
      // the receipt can be classified neither as a first print nor as a duplicate, so printing it
      // would hand the customer a document we cannot account for
      NotificationService.showCustomError(
        t("compliance.cannot-verify-fiscal-state")
      );
      return { shouldAbort: true };
    }
    if (requiresReprint && !navigator.onLine) {
      NotificationService.showCustomError(
        t("compliance.reprint-requires-internet")
      );
      return { shouldAbort: true };
    }
    if (error) {
      LogService.info(
        `Compliance: Error while checking and processing reprint: ${error}`
      );
      return { shouldAbort: true };
    }
    if (reprintResponse && reprintResponse.data) {
      LogService.info(
        `Compliance: Reprint event processed successfully for booking with uuid ${booking.uuid}`,
        { params: reprintResponse.data }
      );
    }
  } catch (error) {
    LogService.info(
      `Compliance: Error while checking and processing reprint: ${error}`
    );
  }

  receiptInfo.isSignatureRequired = true;

  const currentOrderSessionId = BookingModule.currentOrderSessionId;
  const currentOrderSession = booking.orderSessions.find(
    (os) => os.id === currentOrderSessionId
  );

  const signatureInfo = currentOrderSession?.signatureInfoDE;
  const reprintData = reprintResponse?.data;

  if (!signatureInfo && !reprintData) {
    LogService.info("Unable to find signature info for the order session", {
      currentOrderSession,
    });
    receiptInfo.isSigned = false;
  } else {
    receiptInfo.isSigned = true;
  }

  if (signatureInfo || reprintData) {
    receiptInfo.isSigned = !!signatureInfo?.sec || !!reprintData;
    const { sec, fiscalNumber, timestamp, signature } = signatureInfo || {};

    const qrCode = reprintData?.sec
      ? await QRCode.toDataURL(reprintData?.sec)
      : sec
      ? await QRCode.toDataURL(sec)
      : undefined;
    const timeStampToPrint = reprintData?.timestamp || timestamp;
    const formattedTimestamp = dayjs(timeStampToPrint).format(
      "DD-MM-YYYY HH:mm:ss"
    );
    const germanyAdditionalInfo: IGermanyAdditionalInfo = {
      isReprint: !!reprintData,
      qrCode,
      fiscalNumber: reprintData?.fiscalNumber || fiscalNumber,
      timestamp: formattedTimestamp,
      signature: reprintData?.signature || signature,
    };
    receiptInfo.germanyAdditionalInfo = germanyAdditionalInfo;
  }

  if (currentOrderSession && !currentOrderSession.receiptPrinted) {
    currentOrderSession.receiptPrinted = true;

    BookingModule.updateOrderSession({
      bookingUuid: booking.uuid as string,
      orderSessionId: currentOrderSession.id,
      newOrderSession: currentOrderSession,
    });

    LogService.info("Compliance: receipt set to printed");
  }

  return { shouldAbort: false };
}

async function applyItalySignature(
  receiptInfo: IBookingReceipt,
  booking: IProductSlotBooking
): Promise<void> {
  receiptInfo.isSignatureRequired = true;
  const baseOrderSession = booking.orderSessions.find(
    (os) => os.id === BookingModule.currentOrderSessionId
  );
  if (baseOrderSession?.hasOrderLines && baseOrderSession?.signatureInfoIT) {
    receiptInfo.isSigned = true;
    const italyAdditionalInfo: IItalyAdditionalInfo = {
      signature: baseOrderSession.signatureInfoIT.signature,
      documentId: baseOrderSession.signatureInfoIT.govId,
      receiptType: orderTypeToReceiptType(baseOrderSession.orderType),
    };
    receiptInfo.italyAdditionalInfo = italyAdditionalInfo;
  } else {
    if (baseOrderSession && !baseOrderSession.hasOrderLines) {
      receiptInfo.isSignatureRequired = false;
    } else {
      receiptInfo.isSigned = false;
    }
  }
}

async function applySpainSignature(
  receiptInfo: IBookingReceipt,
  booking: IProductSlotBooking
): Promise<ComplianceResult> {
  let reprintResponse;
  try {
    const { requiresReprint, result, error, undeterminedFiscalState } =
      await checkAndProcessReprint(booking);
    reprintResponse = result;
    if (undeterminedFiscalState) {
      // the receipt can be classified neither as a first print nor as a duplicate, so printing it
      // would hand the customer a document we cannot account for
      NotificationService.showCustomError(
        t("compliance.cannot-verify-fiscal-state")
      );
      return { shouldAbort: true };
    }
    if (requiresReprint && !navigator.onLine) {
      NotificationService.showCustomError(
        t("compliance.reprint-requires-internet")
      );
      return { shouldAbort: true };
    }
    if (error) {
      LogService.info(
        `Compliance: Error while checking and processing reprint: ${error}`
      );
      return { shouldAbort: true };
    }
    if (reprintResponse && reprintResponse.data) {
      LogService.info(
        `Compliance: Reprint event processed successfully for booking with uuid ${booking.uuid}`,
        { params: reprintResponse.data }
      );
    }
  } catch (error) {
    LogService.info(
      `Compliance: Error while checking and processing reprint: ${error}`
    );
  }

  receiptInfo.isSignatureRequired = true;

  const currentOrderSessionId = BookingModule.currentOrderSessionId;
  const currentOrderSession = booking.orderSessions.find(
    (os) => os.id === currentOrderSessionId
  );

  const signatureInfo = currentOrderSession?.signatureInfoES;
  const reprintData = reprintResponse?.data;

  if (!signatureInfo && !reprintData) {
    LogService.info("Unable to find signature info for the order session", {
      currentOrderSession,
    });
    receiptInfo.isSigned = false;
  } else {
    receiptInfo.isSigned = true;
  }

  if (signatureInfo || reprintData) {
    receiptInfo.isSigned = !!signatureInfo?.sec || !!reprintData;
    const { sec, fiscalNumber, timestamp, signature } = signatureInfo || {};

    const qrCode = reprintData?.sec
      ? await QRCode.toDataURL(reprintData?.sec)
      : sec
      ? await QRCode.toDataURL(sec)
      : undefined;
    const timeStampToPrint = reprintData?.timestamp || timestamp;
    const formattedTimestamp = dayjs(timeStampToPrint).format(
      "DD-MM-YYYY HH:mm:ss"
    );
    const spainAdditionalInfo: ISpainAdditionalInfo = {
      isReprint: !!reprintData,
      qrCode,
      fiscalNumber: reprintData?.fiscalNumber || fiscalNumber,
      timestamp: formattedTimestamp,
      signature: reprintData?.signature || signature,
    };
    receiptInfo.spainAdditionalInfo = spainAdditionalInfo;
  }

  return { shouldAbort: false };
}

async function applyFranceSignature(
  receiptInfo: IBookingReceipt,
  booking: IProductSlotBooking
): Promise<ComplianceResult> {
  let reprintResponse;
  try {
    const { requiresReprint, result, error, undeterminedFiscalState } =
      await checkAndProcessReprint(booking);
    reprintResponse = result;
    if (undeterminedFiscalState) {
      // the receipt can be classified neither as a first print nor as a duplicate, so printing it
      // would hand the customer a document we cannot account for
      NotificationService.showCustomError(
        t("compliance.cannot-verify-fiscal-state")
      );
      return { shouldAbort: true };
    }
    if (requiresReprint && !navigator.onLine) {
      NotificationService.showCustomError(
        t("compliance.reprint-requires-internet")
      );
      return { shouldAbort: true };
    }
    if (error) {
      LogService.info(
        `Error while checking and processing France reprint: ${error}`
      );
    }
    if (reprintResponse && reprintResponse.data) {
      LogService.info(
        `Reprint event processed successfully for booking with uuid ${booking.uuid}`,
        { params: reprintResponse.data }
      );
    }
  } catch (error) {
    LogService.info(
      `Error while checking and processing France reprint: ${error}`
    );
  }

  receiptInfo.isSignatureRequired = true;

  const currentOrderSessionId = BookingModule.currentOrderSessionId;
  const currentOrderSession = booking.orderSessions.find(
    (os) => os.id === currentOrderSessionId
  );

  const signatureInfo = currentOrderSession?.signatureInfoFR;
  const reprintData = reprintResponse?.data;

  if (!signatureInfo && !reprintData) {
    LogService.info("Unable to find signature info for the order session", {
      currentOrderSession,
    });
    receiptInfo.isSigned = false;
  } else {
    receiptInfo.isSigned = true;
  }

  // When reprinting, company info is taken from the reprint response so the
  // receipt matches the original
  if (reprintResponse?.data) {
    receiptInfo.groupInfo.name = reprintResponse.data.name;
    receiptInfo.groupInfo.country = reprintResponse.data.country;
    receiptInfo.groupInfo.address = `${reprintResponse.data.address} - ${reprintResponse.data.zipCode} ${reprintResponse.data.city}`;
    receiptInfo.groupInfo.street = reprintResponse.data.address;
    receiptInfo.groupInfo.zip = reprintResponse.data.zipCode;
    receiptInfo.groupInfo.city = reprintResponse.data.city;
    receiptInfo.groupInfo.vatNumber = reprintResponse.data.taxId;
  }

  const groupInfo = receiptInfo.groupInfo;
  const seller = UserModule.user;
  const { firstName, lastName } = seller;

  const additionalReceiptInfo: IFranceAdditionalInfo = {
    isReprint: !!reprintResponse?.data,

    signature: reprintData ? reprintData.sec : signatureInfo?.sec,
    originalSignature: reprintData?.originalSec,
    fiscalNumber: reprintData?.fiscalNumber || signatureInfo?.fiscalNumber,
    originalFiscalNumber: reprintData?.originalFiscalNumber,

    nafCode: reprintResponse?.data
      ? reprintResponse?.data.nafCode
      : groupInfo?.nafCode || "",

    siret: reprintResponse?.data
      ? reprintResponse?.data.siretNumber
      : groupInfo?.siret || "",

    siren: reprintResponse?.data
      ? reprintResponse?.data.sirenNumber
      : groupInfo?.siren || "",

    socialCapital: reprintResponse?.data
      ? reprintResponse?.data.capital
      : groupInfo?.socialCapital || "",

    legalForm: reprintResponse?.data
      ? reprintResponse?.data.legalForm
      : groupInfo?.legalForm || "",

    documentTypexOperation: (() => {
      const docType =
        efrDict[reprintData?.documentType] ||
        reprintData?.documentType ||
        signatureInfo?.documentType ||
        "Ticket";
      const opType = signatureInfo?.operationType;
      return docType ? `${docType}${opType ? ` / ${opType}` : ""}` : "";
    })(),

    swVersion: reprintResponse?.data
      ? reprintResponse?.data.currentVersion
      : import.meta.env.VITE_APP_VERSION || "",

    swVersionOriginal: reprintResponse?.data?.originalSoftwareVersion || "",

    posId: reprintResponse?.data
      ? reprintResponse?.data.terminalId
      : DeviceModule.hardwareLocation?.deviceUid || "",

    posIdOriginal: reprintResponse?.data?.originalTerminalId || "",

    lineCount: receiptInfo.products
      .reduce((sum, p) => sum + p.quantity, 0)
      .toString(),

    printCount: reprintResponse?.data ? reprintResponse.data.reprintCount : "1",

    clientId: booking.customerId ? booking.customerId.toString() : "",

    client: CustomersModule.getSelectedCustomerId
      ? `${CustomersModule.getSelectedCustomerFirstName} ${CustomersModule.getSelectedCustomerLastName}`
      : "",

    reprintTimestamp: reprintResponse?.data
      ? formatDateTime(reprintResponse.data.reprintTimestamp, true) +
        " " +
        dayjs(reprintResponse.data.reprintTimestamp).format("HH:mm:ss")
      : "",

    sellerName: reprintResponse?.data
      ? reprintResponse?.data.currentOperatorName
      : `${firstName} ${lastName}` || "",

    originalSellerName: reprintResponse?.data?.originalOperatorName || "",
  };

  receiptInfo.franceAdditionalInfo = additionalReceiptInfo;

  if (currentOrderSession && !currentOrderSession.receiptPrinted) {
    currentOrderSession.receiptPrinted = true;

    BookingModule.updateOrderSession({
      bookingUuid: booking.uuid as string,
      orderSessionId: currentOrderSession.id,
      newOrderSession: currentOrderSession,
    });
  }

  return { shouldAbort: false };
}

async function applyCroatiaSignature(
  receiptInfo: IBookingReceipt,
  booking: IProductSlotBooking
): Promise<ComplianceResult> {
  let reprintResponse;
  try {
    const { requiresReprint, result, error, undeterminedFiscalState } =
      await checkAndProcessReprint(booking);
    reprintResponse = result;
    if (undeterminedFiscalState) {
      // the receipt can be classified neither as a first print nor as a duplicate, so printing it
      // would hand the customer a document we cannot account for
      NotificationService.showCustomError(
        t("compliance.cannot-verify-fiscal-state")
      );
      return { shouldAbort: true };
    }
    if (requiresReprint && !navigator.onLine) {
      NotificationService.showCustomError(
        t("compliance.reprint-requires-internet")
      );
      return { shouldAbort: true };
    }
    if (error) {
      LogService.info(
        `Compliance: Error while checking and processing Croatia reprint: ${error}`
      );
      return { shouldAbort: true };
    }
  } catch (error) {
    LogService.info(
      `Compliance: Error while checking and processing Croatia reprint: ${error}`
    );
  }

  receiptInfo.isSignatureRequired = true;

  const currentOrderSessionId = BookingModule.currentOrderSessionId;
  const currentOrderSession = booking.orderSessions.find(
    (os) => os.id === currentOrderSessionId
  );

  const signatureInfo = currentOrderSession?.signatureInfoHR;
  const reprintData = reprintResponse?.data;

  if (!signatureInfo && !reprintData) {
    LogService.info(
      "Unable to find Croatia signature info for the order session",
      {
        currentOrderSession,
      }
    );
    receiptInfo.isSigned = false;
  } else {
    receiptInfo.isSigned = true;
  }

  if (signatureInfo || reprintData) {
    // Signed when we have a ZKI (online) or a PKP (offline fallback)
    receiptInfo.isSigned = !!(
      signatureInfo?.zki ||
      signatureInfo?.pkp ||
      reprintData
    );

    const qrCodeUrl = reprintData?.qrCode ?? signatureInfo?.qrCodeUrl;
    const qrCode = qrCodeUrl ? await QRCode.toDataURL(qrCodeUrl) : undefined;

    const croatiaAdditionalInfo: ICroatiaAdditionalInfo = {
      isReprint: !!reprintData,
      qrCode,
      oib: signatureInfo?.oib,
      timestamp: reprintData?.timestamp ?? signatureInfo?.timestamp,
      fiscalNumber: reprintData?.fiscalNumber ?? signatureInfo?.fiscalNumber,
      zki: reprintData?.signature ?? signatureInfo?.zki,
      jir: reprintData?.jir ?? signatureInfo?.jir,
      pkp: signatureInfo?.pkp,
    };
    receiptInfo.croatiaAdditionalInfo = croatiaAdditionalInfo;
  }

  return { shouldAbort: false };
}

/**
 * Formats an FDM ticket counter as `event/total label`, e.g. "12/348 NS".
 */
function formatFdmCounter(fdmRef?: {
  eventCounter?: string;
  totalCounter?: string;
  eventLabel?: string;
}): string {
  if (!fdmRef) {
    return "";
  }
  return `${fdmRef.eventCounter}/${fdmRef.totalCounter} ${fdmRef.eventLabel}`;
}

function formatFdmDateTime(dateTime?: string): string {
  return dateTime ? dayjs(dateTime).format("DD-MM-YYYY HH:mm:ss") : "";
}

async function applyBelgiumSignature(
  receiptInfo: IBookingReceipt,
  booking: IProductSlotBooking
): Promise<ComplianceResult> {
  let reprintResponse;
  let orderSessionToReprint: string | null | undefined;
  try {
    const reprintResult = await checkAndProcessReprint(booking);
    reprintResponse = reprintResult.result;
    orderSessionToReprint = reprintResult.orderSessionToReprint;
    if (reprintResult.undeterminedFiscalState) {
      // the receipt can be classified neither as a first print nor as a duplicate, so printing it
      // would hand the customer a document we cannot account for
      NotificationService.showCustomError(
        t("compliance.cannot-verify-fiscal-state")
      );
      return { shouldAbort: true };
    }
    if (reprintResult.error) {
      LogService.info(
        `Compliance: Error while checking and processing Belgium reprint: ${reprintResult.error}`
      );
      return { shouldAbort: true };
    }
    if (
      reprintResult.requiresReprint &&
      (!orderSessionToReprint || !reprintResponse)
    ) {
      LogService.info(
        "Compliance: Belgium reprint required but no reprint response or order session to reprint was found"
      );
      return { shouldAbort: true };
    }
  } catch (error) {
    LogService.info(
      `Compliance: Error while checking and processing Belgium reprint: ${error}`
    );
    return { shouldAbort: true };
  }

  receiptInfo.isSignatureRequired = true;

  const currentOrderSessionId =
    orderSessionToReprint || BookingModule.currentOrderSessionId;
  const currentOrderSession = booking.orderSessions.find(
    (os) => os.id === currentOrderSessionId
  );
  const signatureInfo = currentOrderSession?.signatureInfoBE;
  const reprintData = reprintResponse?.data;

  if (!signatureInfo && !reprintData) {
    LogService.info(
      "Unable to find Belgium signature info for the order session",
      { currentOrderSession }
    );
    receiptInfo.isSigned = false;
    return { shouldAbort: false };
  }

  receiptInfo.isSigned = true;

  const isReprint = !!reprintData;
  const isFullCancellation =
    currentOrderSession?.orderType === OrderType.FULL_CANCELLATION;

  // The FDM's QR payload is `sec`, as for France and Croatia above. On a copy
  // the FDM issues its own, so the reprint's takes precedence.
  const sec = reprintData?.sec ?? signatureInfo?.sec;
  const qrCode = sec ? await QRCode.toDataURL(sec) : undefined;

  const fdmRef = reprintData?.fdmRef ?? signatureInfo?.fdmRef;
  // Present on copies and cancellations: the transaction being referenced.
  //
  // On a copy it has to come off the response, since a pulled order carries no
  // local signatureInfo. `fdmInput` is the request the package sent the FDM to
  // sign the copy, echoed back — and that request's fdmRef IS the original's,
  // resolved server-side from the certification service's archive rather than
  // from anything this terminal remembers.
  const originalFdmRef =
    (reprintData as { fdmInput?: { fdmRef?: FdmRef } })?.fdmInput?.fdmRef ??
    signatureInfo?.originalTransactionFdmRef;

  const seller = UserModule.user;

  const belgiumAdditionalInfo: IBelgiumAdditionalInfo = {
    isReprint,
    // a copy is explicitly not a valid VAT receipt
    isValidVatReceipt: !isReprint,
    isFullCancellation,
    includeOriginalFdmRef: isReprint || isFullCancellation,

    qrCode,
    // On a copy the FDM returns its own signature; the original sale's is
    // printed alongside it. Both originals come off the reprint response, which
    // the package builds from the FDM's archived copy of the transaction — the
    // one source that is still there when the order was pulled rather than sold
    // in this session.
    signature: reprintData?.signature ?? signatureInfo?.signature,
    originalSignature: reprintData?.originalSignature,
    fiscalNumber: reprintData?.fiscalNumber ?? signatureInfo?.fiscalNumber,
    originalFiscalNumber: reprintData?.originalFiscalNumber,

    fdmId: fdmRef?.fdmId,
    fdmDateTime: formatFdmDateTime(fdmRef?.fdmDateTime),
    ticketCounter: formatFdmCounter(fdmRef),
    originalFdmId: originalFdmRef?.fdmId,
    originalFdmDateTime: formatFdmDateTime(originalFdmRef?.fdmDateTime),
    originalTicketCounter: formatFdmCounter(originalFdmRef),

    swVersion: isReprint
      ? reprintData?.originalSoftwareVersion
      : import.meta.env.VITE_APP_VERSION || "",
    posId: reprintData?.posId ?? signatureInfo?.posId,
    terminalId: fdmRef?.terminalId ?? signatureInfo?.terminalId,
    deviceId: fdmRef?.deviceId ?? signatureInfo?.deviceId,
    // the counter is bumped as soon as the event is sent, so the number that
    // was signed is one behind
    transactionNumber: ComplianceModule.transactionNumber - 1,
    fdmVersion: signatureInfo?.fdmVersion,
    posDateTime: reprintData?.posDateTime ?? signatureInfo?.posDateTime,
    locationCbe: reprintData?.locationCbe ?? signatureInfo?.locationCbe ?? "",
    footer: reprintData?.footer ?? signatureInfo?.footer ?? "",

    sellerName:
      reprintData?.originalOperatorName ??
      `${seller.firstName} ${seller.lastName}`,
    sellerId: reprintData?.originalOperatorId ?? seller.id,
    clientId: booking.customerId?.toString() || "",
    client: CustomersModule.getSelectedCustomerId
      ? `${CustomersModule.getSelectedCustomerFirstName} ${CustomersModule.getSelectedCustomerLastName}`
      : "",
    reprintTimestamp: reprintData?.reprintTimestamp
      ? `${formatDateTime(reprintData.reprintTimestamp, true)} ${dayjs(
          reprintData.reprintTimestamp
        ).format("HH:mm:ss")}`
      : "",
  };

  receiptInfo.belgiumAdditionalInfo = belgiumAdditionalInfo;

  // The only record of what actually went on a fiscal document, so a receipt
  // complaint can be answered from a log export rather than guesswork.
  LogService.info("Compliance: Belgium additional receipt info", {
    belgiumAdditionalInfo,
  });

  // A copy or a cancellation prints the "Original ..." labels unconditionally,
  // so a reference that failed to resolve shows up as a blank value rather than
  // an error. What is expected differs by case: a copy carries all five fields
  // from the reprint response, while a cancellation only has the FDM ref off
  // the sale it reverses and legitimately has no original signature or number.
  if (belgiumAdditionalInfo.includeOriginalFdmRef) {
    const expected: [string, unknown][] = [
      ["originalFdmId", belgiumAdditionalInfo.originalFdmId],
      ["originalFdmDateTime", belgiumAdditionalInfo.originalFdmDateTime],
      ["originalTicketCounter", belgiumAdditionalInfo.originalTicketCounter],
      ...(isReprint
        ? ([
            ["originalSignature", belgiumAdditionalInfo.originalSignature],
            [
              "originalFiscalNumber",
              belgiumAdditionalInfo.originalFiscalNumber,
            ],
          ] as [string, unknown][])
        : []),
    ];
    const missing = expected
      .filter(([, value]) => !value)
      .map(([name]) => name);
    if (missing.length) {
      LogService.info(
        "Compliance: Belgium receipt prints the original transaction block but could not resolve it",
        {
          missing,
          isReprint,
          isFullCancellation,
          orderSessionId: currentOrderSessionId,
        },
        LogLevel.Error
      );
    }
  }

  // The FDM computes its own VAT breakdown, which takes precedence over the
  // POS-computed lines. A copy reprints the original's breakdown, so the
  // reprint response wins when there is one.
  const fdmVats: VatCalculation[] =
    reprintData?.vat ?? signatureInfo?.vat ?? [];
  if (fdmVats.length) {
    // Printing the FDM's numbers hides any disagreement with our own, so log it
    // rather than let a drift in the POS VAT maths go unnoticed.
    compareAndLogVatDifferences(fdmVats, receiptInfo.vats || []);

    const fmt = (value: number) => Number(value || 0).toFixed(2);
    receiptInfo.vats = fdmVats.map((vat) => ({
      name: vat.label,
      rate: `${fmt(vat.rate)}%`,
      vatAmount: fmt(vat.vatAmount),
      amountExclVat: fmt(vat.taxableAmount),
      amountInclVat: fmt(vat.totalAmount),
      priceId: [],
      vatIndex: `(${vat.label})`,
      // Belgium prints no purchase/return labels, so the type is not read
      type: VatLineType.Purchase,
    }));
    // Derive the totals from the same source as the breakdown, so the two can
    // never disagree on the printed document.
    receiptInfo.total = fmt(sumArray(fdmVats.map((vat) => vat.totalAmount)));
    receiptInfo.taxTotal = fmt(sumArray(fdmVats.map((vat) => vat.vatAmount)));
    receiptInfo.netTotal = fmt(
      sumArray(fdmVats.map((vat) => vat.taxableAmount))
    );
  }

  // remember that this session was printed, so the next print is a copy
  if (currentOrderSession && !currentOrderSession.receiptPrinted) {
    currentOrderSession.receiptPrinted = true;
    BookingModule.updateOrderSession({
      bookingUuid: booking.uuid as string,
      orderSessionId: currentOrderSession.id,
      newOrderSession: currentOrderSession,
    });
  }

  return { shouldAbort: false };
}

function applyDefaultSignature(receiptInfo: IBookingReceipt): void {
  receiptInfo.isSignatureRequired = false;
  LogService.info(
    `Receipt signature is not required ${receiptInfo.bookingRef}`
  );
}

/**
 * France (NF525) fiscal block for the ORDER model (FIS-517).
 *
 * The fiscal proof comes from the compliance-common-v2 SALE result captured
 * in `ComplianceModule.fiscalReceipts` (bus-driven SALE, tee'd off the
 * subscriber in compliance-v2.ts: order session v2 → EFR →
 * fiscalNumber + signature), the company legal footer from the group's
 * fiscal profile already present on `receiptInfo.groupInfo`. Differences vs
 * the legacy booking-based `applyFranceSignature` (kept above until reprints
 * migrate):
 * - Duplicata reprints are journaled through the v2 PRINT event (0.5.x+):
 *   a repeat print re-issues via the EFR as a DUP-prefixed fiscal number.
 * - The "Code:" line prints the NF525 `Sec` tag (securityCode), grafted from
 *   the SALE outcome when the print outcome lacks it (0.6.x package gap).
 * - documentType derives from the fiscal-number prefix (TIK/DUP); the
 *   operation ("Vente"/"Remboursement") is derived from the session
 *   classification, since v2 does not expose the EFR payload that carries it.
 */
async function applyFranceSignatureV2(
  receiptInfo: IBookingReceipt,
  order: OrderJson
): Promise<ComplianceResult> {
  receiptInfo.isSignatureRequired = true;

  // The receipt documents the order's current session right after completion.
  // For a copy printed later, fall back to the newest previous session that
  // has a captured fiscal receipt.
  const sessions = [
    order.orderSession,
    ...[...order.previousSessions].sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    ),
  ];
  const fiscalizedSession = sessions.find(
    (session) => ComplianceModule.fiscalReceipts[session.id]
  );
  // No locally captured receipt (e.g. after a reload): a reopened order's
  // current session is a fresh EMPTY edit session, so target the newest
  // session that carries lines — the sold one — for the print journaling.
  const receiptSessionId =
    fiscalizedSession?.id ??
    sessions.find((session) => session.orderLines.length > 0)?.id ??
    order.orderSession.id;

  // NF525 print journaling: the first print returns the fiscal info from the
  // package's local receipt store, a repeat print is re-issued as a duplicata
  // through the EFR. Falls back to the locally captured fiscal receipt when
  // the print event cannot resolve (offline, cert unavailable) — printing is
  // never blocked.
  const printOutcome = await processPrintEventV2(receiptSessionId);
  // Read the captured SALE receipt AFTER the print event: the bus-driven
  // SALE lands its outcome asynchronously (the subscriber tee in
  // compliance-v2.ts), normally by the time the print's in-flight wait has
  // resolved — and handleEventOutcomeV2 never downgrades a stored
  // securityCode, so this entry is the best-known merge of the SALE and
  // PRINT proofs even though the print outcome was stored over the same key.
  const capturedSaleReceipt = ComplianceModule.fiscalReceipts[receiptSessionId];
  // The 0.6.x print outcome carries only fiscalNumber + signature (the
  // package's StoredReceipt drops securityCode) — graft the NF525 security
  // code back from the SALE outcome so the printed "Code:" line stays the
  // certified one. Reported to compliance-common-v2 as a gap.
  const fiscalReceipt = printOutcome?.receipt
    ? {
        ...printOutcome.receipt,
        securityCode:
          printOutcome.receipt.securityCode ??
          capturedSaleReceipt?.securityCode,
      }
    : capturedSaleReceipt;

  receiptInfo.isSigned = !!fiscalReceipt;
  if (!fiscalReceipt) {
    LogService.info(
      "Compliance: no France fiscal receipt resolved for this order's sessions, printing unsigned",
      { orderId: order.id, orderSessionId: order.orderSession.id }
    );
  }

  const groupInfo = receiptInfo.groupInfo;
  const { firstName, lastName } = UserModule.user;

  // "TIK:<TL>/<TT>/<DN>" → Ticket, "DUP:..." → Duplicata (see efrDict).
  const documentTypePrefix = fiscalReceipt?.fiscalNumber?.split(":")[0] ?? "";
  const documentType = efrDict[documentTypePrefix] || "Ticket";
  const isReprint = documentTypePrefix === "DUP";

  // Operation type ("Vente" / "Remboursement"): the EFR declares it in
  // `Fis.Payload` — v1 parsed it from there, but the v2 package does not
  // expose the payload yet (reported as a gap). Derive it from the same
  // session classification the fiscal SALE event was built with, so the
  // printed operation always matches what the EFR signed.
  const printedSession =
    sessions.find((session) => session.id === receiptSessionId) ??
    order.orderSession;
  const printedSessionOrderType = classifyOrderSession(
    printedSession,
    order.previousSessions.filter((session) => session.id !== printedSession.id)
  );
  const operationType =
    printedSessionOrderType === OrderType.NEW_SALE ? "Vente" : "Remboursement";

  // Reprint metadata: v1 read it from the package's reprint response;
  // the v2 0.6.x PRINT outcome does not expose the cert print-state yet
  // (originalSoftwareVersion, reprintCount, original operator — reported as
  // a package gap). The reprint moment is the POS's own NOW, and when the
  // SALE receipt is still held locally the original print came from this
  // very build, so its version is the current one.
  const now = dayjs();
  const reprintTimestamp = isReprint
    ? `${formatDateTime(now.toISOString(), true)} ${now.format("HH:mm:ss")}`
    : "";
  const swVersionOriginal =
    isReprint && capturedSaleReceipt
      ? import.meta.env.VITE_APP_VERSION || ""
      : "";

  const additionalReceiptInfo: IFranceAdditionalInfo = {
    isReprint,
    // The printed "Code:" line is the NF525 security code; older locally
    // captured receipts only carry the raw signature.
    signature: fiscalReceipt?.securityCode ?? fiscalReceipt?.signature,
    fiscalNumber: fiscalReceipt?.fiscalNumber,
    nafCode: groupInfo?.nafCode || "",
    siret: groupInfo?.siret || "",
    siren: groupInfo?.siren || "",
    socialCapital: groupInfo?.socialCapital || "",
    legalForm: groupInfo?.legalForm || "",
    documentTypexOperation: `${documentType} / ${operationType}`,
    swVersion: import.meta.env.VITE_APP_VERSION || "",
    swVersionOriginal,
    reprintTimestamp,
    posId: DeviceModule.hardwareLocation?.deviceUid || "",
    lineCount: receiptInfo.products
      .reduce((sum, product) => sum + product.quantity, 0)
      .toString(),
    printCount: "1",
    clientId: CustomersModule.getSelectedCustomerId
      ? String(CustomersModule.getSelectedCustomerId)
      : "",
    client: CustomersModule.getSelectedCustomerId
      ? `${CustomersModule.getSelectedCustomerFirstName} ${CustomersModule.getSelectedCustomerLastName}`
      : "",
    sellerName: `${firstName} ${lastName}`,
  };

  receiptInfo.franceAdditionalInfo = additionalReceiptInfo;
  return { shouldAbort: false };
}

/**
 * Dispatches to the correct country-specific compliance handler based on
 * country code and device configuration. Mutates receiptInfo in place.
 * Returns shouldAbort=true when the caller must not print (e.g. reprint
 * requires internet but the device is offline).
 *
 * TODO(order-migration): the AT/DE/IT/ES/HR fiscal signing (Fiskaly / EFR
 * signatures, reprint events, QR codes) still runs on the legacy booking
 * model and is deferred to the compliance workstream (tracker P1); their
 * dispatches stay commented out until the per-country signers are
 * reimplemented on the order model. France is reimplemented order-based
 * (FIS-517) via {@link applyFranceSignatureV2}.
 */
export async function applyCountryCompliance(
  receiptInfo: IBookingReceipt,
  order: OrderJson
): Promise<ComplianceResult> {
  // if (isAustrianCompliant.value) {
  //   await applyAustriaSignature(receiptInfo, booking);
  //   return { shouldAbort: false };
  // }

  // if (isGermanCompliant.value) {
  //   return await applyGermanySignature(receiptInfo, booking);
  // }

  // if (isItalianCompliant.value) {
  //   await applyItalySignature(receiptInfo, booking);
  //   return { shouldAbort: false };
  // }

  // if (isSpanishCompliant.value) {
  //   return await applySpainSignature(receiptInfo, booking);
  // }

  if (isFrenchCompliant.value) {
    return await applyFranceSignatureV2(receiptInfo, order);
  }

  // if (isCroatianCompliant.value) {
  //   return await applyCroatiaSignature(receiptInfo, booking);
  // }

  // if (isBelgianCompliant.value) {
  //   return await applyBelgiumSignature(receiptInfo, booking);
  // }

  applyDefaultSignature(receiptInfo);
  return { shouldAbort: false };
}
